import { useEffect, useState } from 'react';
import type { Tv2mLinkContext } from '@apm/contracts';
import { isExternalCheckoutUrl, openExternalCheckout } from '../checkout.js';
import type { CheckoutDeps, CheckoutExit, CheckoutTracking } from '../checkout.js';
import { TriggerBadge } from '../components/TriggerBadge.js';
import { fetchAdCreative } from '../creative.js';
import { formatPrice } from '@apm/i18n';
import { markLeftForCheckout } from '../left-for-checkout.js';
import { fetchProduct } from '../product.js';
import { useLocale, useT } from '../i18n/LocaleProvider.js';
import type { MessageKey } from '../i18n/ko.js';

/**
 * 상품 화면 — Deep Link 의 도착지이자 **Tier 0 시트의 알맹이다** (`ProductSheet` 가 이것을
 * 그대로 감싼다). 딥링크로 온 사람과 같은 기기에서 누른 사람이 다른 화면을 볼 이유가 없다.
 *
 * **진입 이벤트는 여기서 보내지 않는다.** SDK 의 `handleDeepLink()` 가 유입 경로에 따라
 * 이미 낸다(`ENTRY_EVENTS`: `tv2m` → `push_clicked`, `tv2m_same_device` →
 * `same_device_handoff_clicked`, Deferred 둘 → `product_page_opened`). 화면이 한 번 더
 * 세면 같은 사건이 두 벌이 되고 전환율이 부풀려진다.
 *
 * **상품 정보는 없어도 그린다.** BLOOM Server 조회는 화면을 풍부하게 할 뿐 전환 경로의
 * 조건이 아니다 — 서버가 꺼져 있어도 시청자가 온 이유(상품 id·쿠폰·유입 경로)는 보여야
 * 한다. **다만 B10 부터는 조회 실패가 구매 버튼까지 막는다** — 판매처 주소(`deepLink`)가
 * 그 응답에만 있기 때문이고, 그 사실을 숨기지 않고 화면에 적는다.
 *
 * ── B10 이 바꾼 것 ─────────────────────────────────────────
 *
 * `[구매하기]` 가 데모용 완료 화면으로 가던 자리에 **`[판매처로 이동]`** 이 들어왔다.
 * 이것이 **BLOOM 을 떠나는 유일한 순간**이고(절대 규칙 14), 새 탭이므로 뒤에 남은 방송은
 * 계속 재생된다. 완료 화면은 그 뒤에만 닿는다 — 나가지도 않은 사람에게 "구매가
 * 완료되었습니다" 를 보여 줄 이유가 없다.
 *
 * ── B11 이 더한 것: 상품을 어디서 읽는가가 유입 경로로 갈린다 ──
 *
 * **광고 상품은 BLOOM 카탈로그에 없다** (`docs/12` §2.3). 그래서 `triggerType === 'ad'` 로
 * 들어온 화면은 BLOOM Server 대신 TV2M 의 `GET /v1/creatives/{campaignId}` 를 읽는다.
 *
 * **화면을 두 벌로 만들지 않는다.** 광고로 들어온 사람과 방송으로 들어온 사람이 다른
 * 구매 화면을 볼 이유가 없고, 두 벌이면 다음 변경에서 한쪽만 고쳐진다 — B10 이
 * `[판매처로 이동]` 을 붙일 곳이 하나였던 것과 같은 이유다 (`ProductSheet` 의 주석).
 * 갈리는 것은 **읽어 오는 곳과 나갈 곳** 둘뿐이다: `Product.deepLink` ↔
 * `AdCreative.landingUrl`.
 */
/**
 * ★ **사본을 읽을 것인가, BLOOM 카탈로그를 읽을 것인가** (v23 O12 · `docs/47` §11 의 ★).
 *
 * ── 이 함수가 없어서 무슨 일이 날 뻔했나 ──────────────────
 *
 * 여기는 v21 까지 **`triggerType === 'ad'` 하드코딩**이었다. `useTv2m` 이
 * `result.triggerType` 을 그대로 넘기므로 **`content_offer` 는 `else` 로 떨어지고**,
 * 그 `productId` 는 브로커의 `offerId` 라 **BLOOM 이 그것을 갖고 있지 않다**
 * (O9 가 **일부러** 그렇게 만든다). 결과는 `state === 'missing'` — **빈 화면**이다.
 *
 * ★ **그리고 이 자리는 컴파일이 못 잡는다.** 삼항이지 `Record<TriggerType, …>` 이 아니라
 * `TRIGGER_TYPES` 를 넓혔을 때 **세 곳을 짚어 준** 그 그물 밖에 있다. **사람이 찾아야 했고,
 * 설계 검증이 찾았다.**
 *
 * ── 조건을 「종류」가 아니라 「어디에 있는가」로 바꾼다 ──────
 *
 * `'ad'` 비교를 **「BLOOM 카탈로그에 없는 종류인가」**로 바꾼다. 그러면 **넷째 종류가
 * 생겨도 같은 자리 한 곳**이고, 그때 이 함수가 컴파일로 그것을 요구한다
 * (`Record<TriggerType, boolean>` 이라 값이 늘면 여기가 깨진다).
 *
 * ── 증상이 조용하다는 것이 이 자리의 성질이다 ────────────────
 *
 * 리시버·BLOOM·TV2M 이 전부 정상이고 **알림도 간다.** **폰에서 눌렀을 때만 빈 화면**이고,
 * 그것은 「TV2M 이 스냅샷을 안 남겼다」와 **화면상 완전히 같은 증상**이다 —
 * O16 의 검사 29 와 변이 10 이 그 둘을 가른다.
 */
const READS_SNAPSHOT: Record<Tv2mLinkContext['triggerType'], boolean> = {
  // 광고 소재는 BLOOM 카탈로그에 없다 (`docs/12` §2.3).
  ad: true,
  // 제휴 상품도 없다 — 그리고 브로커에는 다시 물을 창이 없다 (절대 규칙 63).
  content_offer: true,
  // 방송 중인 상품은 BLOOM 의 것이다.
  channel_content: false,
};

function readsSnapshot(triggerType: Tv2mLinkContext['triggerType']): boolean {
  return READS_SNAPSHOT[triggerType];
}

export function ProductPage({
  context,
  checkout,
  tracking,
  tv2mServerUrl,
  onContinue,
  continueLabelKey = 'product.continueWatching',
  onPurchaseDone,
}: {
  context: Tv2mLinkContext;
  /** 이벤트를 내고 보내는 통로. `Tv2mApi` 가 이 모양을 만족한다 */
  checkout: CheckoutDeps;
  /** 추적 4파라미터 중 이 앱이 아는 둘. 시청 세션이 없으면 비어 있다 (`checkout.ts`) */
  tracking: CheckoutTracking;
  /**
   * 광고 소재를 읽을 곳 (B11). **`config` 에서 읽을 수 없다** — TV2M 주소는 빌드 시점이
   * 아니라 `GET /v1/bloom/config` 로 온다 (`docs/12` §2.4).
   */
  tv2mServerUrl: string;
  /** 시트면 닫기, 독립 화면이면 방송으로. **구매를 그만두는 것이 이탈이 되면 안 된다** */
  onContinue: () => void;
  continueLabelKey?: MessageKey;
  /**
   * 판매처를 새 탭으로 연 **뒤에만** 닿는 데모 완료 화면.
   *
   * ★ **없을 수 있다** (v21/F13). 시청 화면의 시트에는 **갈 곳이 없다** — 거기서 `/done`
   * 으로 옮기면 `WatchScreen` 이 내려가 방송이 끊기고, 무엇보다 **나갔는지 확인하지 못한
   * 이탈을 「구매 완료」라고 말하게 된다.** 아이폰 홈 화면 앱에서 `window.open` 이
   * truthy 를 주고도 아무것도 안 열리는 경우가 그것이고, 실기에서 그렇게 나왔다.
   *
   * 딥링크로 **이 화면에 도착한** 폰에는 그대로 있다 — 그쪽은 방송이 뒤에 없다.
   */
  onPurchaseDone?: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const product = useProductView(context, tv2mServerUrl);
  const [leaving, setLeaving] = useState(false);
  const [exit, setExit] = useState<CheckoutExit | null>(null);

  /*
   * ★ **나갈 수 있는 주소만 딥링크다** (v21/F13).
   *
   * 조회가 준 값이 **비어 있을 수 있다** — 일반 채널 회차의 `deepLink` 는 빈 문자열이고
   * 그것이 규칙이다. 그 값을 그대로 쓰면 `buildCheckoutUrl` 이 **상대 URL** 을 만들고,
   * 설치된 앱은 그것을 자기 오리진으로 읽어 **자기 시작 게이트로 간다**(`checkout.ts` 의
   * `isExternalCheckoutUrl`). `null` 로 좁히면 아래의 이미 있는 길로 떨어진다 —
   * 버튼이 비활성이고 이유가 화면에 적힌다.
   */
  const checkoutUrl = product.item?.checkoutUrl ?? null;
  const deepLink = checkoutUrl !== null && isExternalCheckoutUrl(checkoutUrl) ? checkoutUrl : null;
  // 딥링크가 실어 온 쿠폰이 먼저다. 없으면 소재의 것을 쓴다 — 광고 화면을 직접 열면
  // (설치 후 복원처럼) 쿠폰 파라미터가 없을 수 있는데, 쿠폰은 소재에도 있다.
  const coupon = context.coupon ?? product.item?.couponCode ?? null;

  /**
   * 판매처로 나간다. 순서와 팝업 폴백은 `checkout.ts` 가 갖는다 —
   * **이 화면은 두 곳(시트·독립 화면)에서 그려지므로** 여기에 절차를 두면 두 벌이 된다.
   */
  const goToStore = () => {
    if (deepLink === null || leaving) return;
    setLeaving(true);
    /*
     * ★ **떠나기 직전에 표시를 남긴다** (v21/F13).
     *
     * 설치된 앱에서는 외부로 나가는 **모든** 방법이 앱을 그 자리에서 대체한다 — 실기로
     * 셋을 다 재 봤다(`docs/46` §3.9). 그래서 돌아오면 앱이 다시 뜨고 **진입 게이트**다.
     * 게이트는 자동재생 정책 때문에 없앨 수 없지만, 그것이 「처음 보기」로 보일 이유는
     * 없다 — 채널과 지점은 이미 살아 있다.
     *
     * **`await` 앞에 둔다.** 이 뒤로는 이 페이지가 언제 사라질지 모른다.
     */
    markLeftForCheckout();
    void openExternalCheckout(checkout, {
      deepLink,
      productId: context.productId,
      ...(context.matchId === undefined ? {} : { matchId: context.matchId }),
      tracking,
    })
      .then(setExit)
      .finally(() => {
        // 같은 탭으로 떠났으면 이 줄은 대개 실행되지 않는다. 실행되는 경우(새 탭)에만
        // 뜻이 있고, 그때는 버튼을 되살려 두 번째 시도를 막지 않는다.
        setLeaving(false);
      });
  };

  return (
    <>
      <TriggerBadge source={context.source} triggerType={context.triggerType} />

      {product.item?.thumbnailUrl ? (
        <img className="product__thumb" src={product.item.thumbnailUrl} alt="" />
      ) : (
        <div className="product__thumb" />
      )}

      <div>
        <h2 className="product__title" data-i18n="data">
          {product.item?.title ?? t('product.loadingTitle')}
        </h2>
        {product.item !== null && (
          <p className="product__price">{formatPrice(product.item.price, locale)}</p>
        )}
      </div>

      {coupon !== null && (
        <div className="product__coupon">{t('product.coupon', { code: coupon })}</div>
      )}

      {/*
        조회가 실패한 이유를 숨기지 않는다. `ready` 가 아닌 상품은 T9 의 필터에서
        404 이고(절대 규칙 1 의 정신), 그것은 화면의 오류가 아니라 **편성 상태**다.
        광고 경로라면 소재가 없거나 만료된 것이다 — 어느 쪽이든 시청자가 할 일은 같다.
      */}
      {product.state === 'missing' && (
        <p className="card__text">
          {readsSnapshot(context.triggerType)
            ? t('product.creativeLoadFailed')
            : t('product.loadFailed')}
        </p>
      )}

      <div className="spacer" />

      {/*
        **판매처 주소는 상품 조회에만 있다.** 없으면 갈 곳이 없으므로 버튼을 눌리게
        두지 않는다 — 눌리는데 아무 일도 안 일어나는 버튼이 더 나쁘다. 이유를 함께 적는다.
      */}
      {deepLink === null && product.state !== 'loading' && (
        <p className="product__note">{t('product.noDeepLink')}</p>
      )}

      {exit === null ? (
        <button
          type="button"
          className="button"
          data-testid="go-to-store"
          disabled={deepLink === null || leaving}
          onClick={goToStore}
        >
          {leaving ? t('product.leaving') : t('product.goToStore')}
        </button>
      ) : (
        <>
          {/*
            나갔고 **이 탭은 그대로 살아 있다.** 시트라면 그 뒤에서 방송이 계속 재생되는
            중이다 (절대 규칙 14).

            ★ **문구가 둘이다** (v21/F13). `new_tab` 은 창을 받은 **관측된 성공**이라
            「열었습니다」라고 말해도 된다. `handed_off` 는 앵커를 눌러 브라우저에 넘긴
            것이고 **열렸는지 원리적으로 알 수 없다** — 그 자리에서 「열었습니다」라고 하면
            안 열린 사람에게 거짓말이 된다. 설치된 앱은 대개 이쪽이다.
          */}
          <p className="product__note">
            {t(exit === 'new_tab' ? 'product.openedInNewTab' : 'product.handedOffToStore')}
          </p>
          {/*
            ★ **갈 곳이 있을 때만 그린다** (v21/F13). 시트에는 없다 — 위 prop 주석.

            ★★ **문구가 시청자의 말이지 앱의 단정이 아니다.** 한국어가 「구매를
            마쳤습니다」라는 **완결된 평서문**이었고, 그래서 앱이 *「당신의 구매가
            완료되었습니다」*라고 말하는 것으로 읽혔다 — 아무것도 사지 않았는데.
            실기에서 그 질문이 나왔고, 기록은 **`purchase_completed` 0건**이었다
            (이탈 41 · 알림 클릭 22). 읽히지 않는 버튼이었다는 뜻이다.

            **영어 사전은 그때도 `"I've finished buying"` 이었다** — 두 사전의 뜻이
            갈려 있었고 **타입 검사는 키만 보므로 그것을 못 본다**(절대 규칙 24 가
            「사전을 거치는 문구만 덮는다」고 적은 자리의 또 다른 얼굴이다).
            지금은 둘 다 **명사구 + (데모)** 이고, 완료 화면의 `done.title`
            (「완료 화면 (데모)」)과 짝이 맞는다.
          */}
          {onPurchaseDone !== undefined && (
            <button type="button" className="button" onClick={onPurchaseDone}>
              {t('product.purchaseDone')}
            </button>
          )}
        </>
      )}

      <button type="button" className="button button--secondary" onClick={onContinue}>
        {t(continueLabelKey)}
      </button>

      <dl className="kv">
        <dt>{t('product.idLabel')}</dt>
        <dd>{context.productId}</dd>
        <dt>campaign</dt>
        <dd>{context.campaignId}</dd>
        {/*
          광고 경로에서만 채워진다 (B11). 채널 콘텐츠에는 광고주가 없으므로 이 줄이
          보인다는 것 자체가 「이 화면이 TV2M 카탈로그를 읽었다」의 표시다.
        */}
        {product.item?.advertiserId != null && (
          <>
            <dt>advertiser</dt>
            <dd>{product.item.advertiserId}</dd>
          </>
        )}
        {context.matchId !== undefined && (
          <>
            <dt>match</dt>
            <dd>{context.matchId}</dd>
          </>
        )}
      </dl>
    </>
  );
}

/**
 * 화면이 그리는 상품. **두 카탈로그를 같은 모양으로 좁힌 것이다.**
 *
 * BLOOM Server 의 `ProductPublicResponse` 와 TV2M 의 `AdCreativeResponse` 는 겹치는 부분이 많지만
 * 같은 타입이 아니고, 그래야 한다 — 앞엣것에는 재생 정보(`videoUrl`·`skip`)가 있고
 * 뒤엣것에는 광고주가 있다. 화면이 실제로 쓰는 것만 여기서 하나로 만든다.
 */
interface ProductView {
  title: string;
  price: number;
  thumbnailUrl: string;
  /** **나갈 곳.** BLOOM Server 는 `deepLink`, 광고는 `landingUrl` — 이름이 다르고 뜻이 같다 */
  checkoutUrl: string;
  /** 광고 경로에서만 채워진다 */
  advertiserId?: string | null;
  couponCode?: string | null;
}

interface ProductState {
  state: 'loading' | 'found' | 'missing';
  item: ProductView | null;
}

/**
 * 상품을 한 번 읽는다. **어디서 읽는지는 유입 경로가 정한다** (B11).
 *
 * - `channel_content` → BLOOM Server 의 공개 단건 조회 (T9)
 * - `ad` → TV2M 의 광고 소재 조회. **광고 상품은 BLOOM 카탈로그에 없다** (`docs/12` §2.3)
 *
 * **재시도하지 않는다** — 실패는 대개 "그 상품이 지금 방송에 노출되지 않는다"(T9 의 필터)
 * 이거나 "그 캠페인이 끝났다" 이고, 두 상태 모두 두드린다고 바뀌지 않는다.
 *
 * `AbortController` 로 정리한다. `StrictMode` 가 효과를 두 번 돌리므로 앞의 요청이
 * 늦게 도착해 뒤의 결과를 덮는 창이 실재한다.
 */
function useProductView(context: Tv2mLinkContext, tv2mServerUrl: string): ProductState {
  const [state, setState] = useState<ProductState>({ state: 'loading', item: null });
  const { productId, campaignId, triggerType } = context;

  useEffect(() => {
    const controller = new AbortController();
    setState({ state: 'loading', item: null });

    const load: Promise<ProductView | null> = readsSnapshot(triggerType)
      ? fetchAdCreative(tv2mServerUrl, campaignId, controller.signal).then((creative) =>
          creative === null
            ? null
            : {
                title: creative.title,
                price: creative.price,
                thumbnailUrl: creative.thumbnailUrl,
                checkoutUrl: creative.landingUrl,
                advertiserId: creative.advertiserId,
                couponCode: creative.couponCode,
              },
        )
      : fetchProduct(productId, controller.signal).then((product) =>
          product === null
            ? null
            : {
                title: product.title,
                price: product.price,
                thumbnailUrl: product.thumbnailUrl,
                checkoutUrl: product.deepLink,
              },
        );

    void load.then((item) => {
      if (controller.signal.aborted) return;
      setState(item === null ? { state: 'missing', item: null } : { state: 'found', item });
    });

    return () => {
      controller.abort();
    };
  }, [productId, campaignId, triggerType, tv2mServerUrl]);

  return state;
}
